import useSWR from "swr";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { SortableDealTable } from "../forecast-experience/DealsByOutcome";
import { useAppStore } from "@/stores";
import { supabaseClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { requestRevenueIntelligenceAnswerComposition } from "../lib/revenue-intelligence-semantic-planner";
import { getRevenueIntelligenceChatAccessToken } from "../lib/revenue-intelligence-chat-session";
import { TARGET_OUTLOOK_READOUT_PROMPT, splitOutlookReadoutClaims, type buildOutlookReadout } from "./readout";
import { requestOutlookReadout } from "./readout-request";
import { OUTLOOK_COPY } from "./copy";
import { usesAnalysisMoneyUnits, type DisplayCurrency } from "../lib/moneyDisplay";
type ReadoutOpportunity = ReturnType<typeof buildOutlookReadout>["opportunities"][number];

// A caller of the existing factual-answer composer, not a new API or planner.
// All selection, arithmetic and conclusions are complete before this boundary.
export function ManagementReadout({ readout, ready, onInspectPipeline, sortableDeals = false, opportunityFacts, analysisCurrency }: {
  readout: ReturnType<typeof buildOutlookReadout>; ready: boolean; onInspectPipeline?: () => void; sortableDeals?: boolean;
  analysisCurrency?: DisplayCurrency;
  opportunityFacts?: ReadonlyMap<string, { impact: number; amount: string; probability: number; action: string }>;
}) {
  const user = useAppStore(state => state.currentUser);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const opportunityColumns = useMemo<ColumnDef<ReadoutOpportunity>[]>(() => {
    const columns: ColumnDef<ReadoutOpportunity>[] = [
      { accessorKey: "name", header: "Deal" }, { accessorKey: "owner", header: "Owner" },
    ];
    if (opportunityFacts) columns.push(
      { id: "action", header: "Action", accessorFn: row => opportunityFacts.get(row.id)?.action },
      { id: "additional", header: "Additional if won", accessorFn: row => opportunityFacts.get(row.id)?.impact,
        cell: ({ row }) => opportunityFacts.get(row.original.id)?.amount ?? "—" },
      { id: "probability", header: "Chance this period", accessorFn: row => opportunityFacts.get(row.id)?.probability,
        cell: ({ row }) => {
          const value = opportunityFacts.get(row.original.id)?.probability;
          return value === undefined ? "—" : `${Math.round(value * 100)}%`;
        } },
    );
    return columns;
  }, [opportunityFacts]);
  const allowed = user && ["admin", "super_admin"].includes(user.role);
  const packet = JSON.stringify(readout.packet);
  const result = useSWR(ready && allowed ? ["target-outlook-readout", user.company_id, user.id, packet, TARGET_OUTLOOK_READOUT_PROMPT,
    ...(analysisCurrency === undefined ? [] : ["analysis-money-millions-v1"])] : null,
    async ([, companyId, , approvedAnswer]) => {
      const accessToken = await getRevenueIntelligenceChatAccessToken(() => supabaseClient.auth.getSession());
      if (!accessToken) throw new Error("Sign in to prepare the management readout.");
      const composed = await requestOutlookReadout(signal => requestRevenueIntelligenceAnswerComposition({
        accessToken, approvedAnswer, companyId: String(companyId),
        conversationId: "target-outlook",
        question: TARGET_OUTLOOK_READOUT_PROMPT,
        signal,
      }));
      if (analysisCurrency !== undefined && !usesAnalysisMoneyUnits(composed.answer, analysisCurrency)) {
        throw new Error("Written summary changed the display unit.");
      }
      return composed;
    }, { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false,
      keepPreviousData: false, errorRetryCount: 0, dedupingInterval: 86_400_000 });
  return <section aria-labelledby="management-readout" className="space-y-4">
    <h2 id="management-readout" className="text-base font-medium text-[#231942]">Management readout</h2>
    <p className="max-w-[85ch] text-base leading-7 text-gray-700">{splitOutlookReadoutClaims(result.data?.answer ?? readout.fallback, readout.opportunities.length).map((claim, index) => <span key={index}>
      {index > 0 && " "}{claim.before}{claim.hasOpportunityDetails
        ? <button type="button" aria-haspopup="dialog" className="text-left underline decoration-[#9F86C0] decoration-dotted underline-offset-4 hover:text-[#5E548E] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#5E548E]" onClick={() => setDetailsOpen(true)}>{claim.linkText}</button>
        : null}{claim.after}
    </span>)}</p>
    {readout.futurePrimary && onInspectPipeline && <button type="button" onClick={onInspectPipeline} className="text-sm text-gray-600 underline decoration-dotted underline-offset-4">{OUTLOOK_COPY.pipeline.inspect}</button>}
    {!result.data && <div className="flex items-center gap-3 text-xs text-gray-500" role="status">
      <span>{result.isLoading || result.isValidating || !ready ? "Preparing the written summary; computed conclusions shown." : "Written summary unavailable; computed conclusions shown."}</span>
      {result.error && !result.isValidating && <Button size="sm" variant="ghost" onClick={() => void result.mutate()}>Retry summary</Button>}
    </div>}
    <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}><DialogContent className={`max-h-[80vh] overflow-y-auto ${opportunityFacts ? "sm:max-w-4xl" : ""}`}><DialogHeader>
      <DialogTitle>Identified opportunities</DialogTitle>
      <DialogDescription>{readout.packet.period} · {readout.packet.bestCombinedOutcome.additional} additional if the identified opportunities close within the period</DialogDescription>
    </DialogHeader>{sortableDeals ? <SortableDealTable rows={readout.opportunities} columns={opportunityColumns} /> : <ul className="divide-y text-sm">{readout.opportunities.map(opportunity => <li key={opportunity.id} className="py-3">
      <p className="font-medium">{opportunity.name}</p><p className="mt-1 text-gray-500">{opportunity.owner}</p>
    </li>)}</ul>}</DialogContent></Dialog>
  </section>;
}
